// PipelineGlyph — how a character is DRAWN, everywhere a character is named.
//
// Extracted from CharacterLauncher when the menubar consolidation landed, because
// the menubar's character chip has to identify the active character the same way
// the picker does. Two drawings of one concept would drift, and the whole point of
// the glyph is that a character looks the same wherever you meet it.
//
// A pipeline diagram instead of a portrait: the generic bust it replaced was
// identical on every card — decoration, not information. And a real portrait would
// not separate this catalog anyway (the names are largely authoring fixtures), so
// what decides a pick is which pipeline the character runs on and whether it has
// geometry at all. That is what this draws.
//
// `status` comes from characterRigStatus.js (classifyRigStatus), NOT from
// character.renderer — that field is "" for almost every row, so reading it would
// badge every OGS character as a rig character and then flag it "NO RIG" for
// legitimately having no rig GLB.

import React from 'react'

// Typography constants moved to the shared style module (src/lib/studioStyles.js)
// and now ride the --m-font/--m-mono token contract; re-exported here because
// this file was their historical home and the shell chrome imports from it.
export { MONO, UI_FONT } from '../lib/studioStyles.js'

/** The three pipelines that carry a positive identity: a mesh rig, a splat rendered
 *  ON-DEVICE, and one streamed from a GPU server. 'missing' and 'unknown' are
 *  deliberately absent: they are the ABSENCE of one, handled below. */
export const STATUS = {
  rig: { label: 'RIG', token: '--m-wire-anim' },
  splat: { label: 'GSPLAT', token: '--m-wire-output' },
  stream: { label: 'STREAM', token: '--m-wire-state' },
}

// A step below --m-fg-3, for the not-yet-probed glyph: it should read as absent
// information rather than competing with the text.
const UNKNOWN_STROKE = 'color-mix(in srgb, var(--m-fg-3) 55%, transparent)'

/** The colour that stands for a pipeline. A rigless character is a warning. */
export function statusTone(status) {
  if (status === 'missing') return 'var(--m-warn)'
  return `var(${STATUS[status]?.token ?? '--m-fg-3'})`
}

export function PipelineGlyph({ status, size }) {
  const tone = statusTone(status)
  const stroke = { fill: 'none', stroke: tone, strokeWidth: 1.3, strokeLinecap: 'round' }

  if (status === 'splat') {
    // Point cloud — gaussians, no skeleton.
    const pts = [[8, 20], [14, 11], [13, 25], [20, 7], [19, 17], [20, 27], [26, 12], [27, 22], [32, 18], [24, 32], [11, 32]]
    return (
      <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden="true">
        {pts.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i % 3 === 0 ? 2.4 : 1.6} fill={tone} opacity={0.35 + (i % 4) * 0.2} />
        ))}
      </svg>
    )
  }
  // No 'stream' glyph: `classifyRigStatus` cannot return it any more. Streaming is
  // a way to render an OGS character, chosen per session with ?stream=1, so it is
  // not a property of a catalogue row and there is nothing for a row badge to
  // claim. A streamed character wears its 'splat' glyph, which is what its bundle
  // IS whoever renders it.
  if (status === 'missing') {
    // An empty frame: the shape of a character with nothing in it.
    return (
      <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden="true">
        <rect x="9" y="9" width="22" height="22" rx="3" strokeDasharray="3 3" {...stroke} />
        <path d="M15 15 L25 25 M25 15 L15 25" {...stroke} strokeWidth="1.1" opacity=".65" />
      </svg>
    )
  }
  if (status === 'rig') {
    // Joint chain.
    return (
      <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden="true">
        <path d="M20 7 L20 19 M20 19 L12 27 M20 19 L28 27 M20 12 L13 15 M20 12 L27 15" {...stroke} />
        {[[20, 7], [20, 19], [12, 27], [28, 27], [13, 15], [27, 15]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="2.1" fill={tone} />
        ))}
      </svg>
    )
  }
  // 'unknown' — the probe has not answered yet. Deliberately says nothing rather
  // than guessing a pipeline.
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden="true">
      <circle cx="20" cy="20" r="9" strokeDasharray="2 3" {...stroke} stroke={UNKNOWN_STROKE} />
    </svg>
  )
}

export default PipelineGlyph
